import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import ProgressBar from '../../components/ProgressBar';
import { Input, TextArea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { getImageUrl } from '../../utils/imageUtils';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin: 2rem 0 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  label { display: block; margin-bottom: 0.5rem; font-weight:500; }
`;

const TeamAdmin = () => {
  const [header, setHeader] = useState({ title: '', description: '', image: '' });
  const [members, setMembers] = useState([]);
  const [view, setView] = useState('list');
  const [currentMember, setCurrentMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploadProgress, setUploadProgress] = useState({ header: 0, member: 0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [h, m] = await Promise.all([api.get('/team/header'), api.get('/team/members')]);
      setHeader(h);
      setMembers(m);
    } catch (err) { console.error(err); }
  };

  const saveHeader = async () => {
    setSaving(true);
    try {
      await api.put('/team/header', { title: header.title, description: header.description });
      setMsg('Header saved');
    } catch (err) { setMsg('Error saving header'); }
    finally { setSaving(false); }
  };

  const handleHeaderImage = async (file) => {
    if (!file) return;
    setUploadProgress(prev => ({ ...prev, header: 0 }));
    
    const timer = setInterval(() => {
        setUploadProgress(prev => {
            if(prev.header < 90) return { ...prev, header: prev.header + 10 };
            return prev;
        });
    }, 100);

    try {
        const fd = new FormData(); fd.append('image', file);
        const res = await api.upload('/team/header/image', fd);
        
        clearInterval(timer);
        setUploadProgress(prev => ({ ...prev, header: 100 }));
        
        setHeader(res);
        setTimeout(() => setUploadProgress(prev => ({ ...prev, header: 0 })), 500);
    } catch (err) {
        clearInterval(timer);
        setUploadProgress(prev => ({ ...prev, header: 0 }));
        console.error('Header image upload failed', err);
    }
  };

  const handleMemberSave = async () => {
    setSaving(true);
    try {
      if (currentMember._id) {
        await api.put(`/team/members/${currentMember._id}`, { 
            name: currentMember.name, 
            role: currentMember.role,
            description: currentMember.description 
        });
      } else {
        await api.post('/team/members', { 
            name: currentMember.name, 
            role: currentMember.role,
            description: currentMember.description 
        });
      }
      setView('list');
      loadData();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
        await api.delete(`/team/members/${id}`);
        loadData();
    } catch (err) {
        console.error(err);
    }
  };

  const handleMemberImage = async (file) => {
    if (!file) return;
    setUploadProgress(prev => ({ ...prev, member: 0 }));
    
    const timer = setInterval(() => {
        setUploadProgress(prev => {
            if(prev.member < 90) return { ...prev, member: prev.member + 10 };
            return prev;
        });
    }, 100);

    try {
        const fd = new FormData(); fd.append('image', file);
        const res = await api.upload(`/team/members/${currentMember._id}/image`, fd);
        
        clearInterval(timer);
        setUploadProgress(prev => ({ ...prev, member: 100 }));
        
        setCurrentMember(res);
        setTimeout(() => setUploadProgress(prev => ({ ...prev, member: 0 })), 500);
    } catch (err) {
        clearInterval(timer);
        setUploadProgress(prev => ({ ...prev, member: 0 }));
        console.error('Member image upload failed', err);
    }
  };

  if (view === 'form') {
    return (
      <Container>
        <h2>{currentMember._id ? 'Edit Member' : 'Add Member'}</h2>
        <Card>
          <CardContent>
            <FormGroup><label>Name</label><Input value={currentMember.name} onChange={e=>setCurrentMember({...currentMember, name:e.target.value})} /></FormGroup>
            <FormGroup><label>Role</label><Input value={currentMember.role} onChange={e=>setCurrentMember({...currentMember, role:e.target.value})} /></FormGroup>
            <FormGroup><label>Bio</label><TextArea value={currentMember.description} onChange={e=>setCurrentMember({...currentMember, description:e.target.value})} /></FormGroup>
            {currentMember._id && (
                <FormGroup>
                    <label>Photo</label>
                    <ImageUploader previewUrl={currentMember.image} onImageSelect={handleMemberImage} />
                    {uploadProgress.member > 0 && <ProgressBar progress={uploadProgress.member} />}
                </FormGroup>
            )}
            <Button onClick={handleMemberSave} disabled={saving}>{saving?'Saving...':'Save'}</Button>
            <Button onClick={()=>setView('list')} $variant="secondary" style={{marginLeft:'1rem'}}>Cancel</Button>
            {!currentMember._id && <p style={{fontSize:'0.9rem', color:'#666'}}>Add photo after creating member.</p>}
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h2>Manage Team</h2>
      {msg && <div style={{color:'green'}}>{msg}</div>}

      <Card style={{marginBottom:'2rem'}}>
        <CardContent>
          <h3>Header</h3>
          <FormGroup><label>Title</label><Input value={header.title} onChange={e=>setHeader({...header, title:e.target.value})} /></FormGroup>
          <FormGroup><label>Description</label><TextArea value={header.description} onChange={e=>setHeader({...header, description:e.target.value})} /></FormGroup>
          <ImageUploader previewUrl={header.image} onImageSelect={handleHeaderImage} />
          {uploadProgress.header > 0 && <ProgressBar progress={uploadProgress.header} />}
          <Button onClick={saveHeader} style={{marginTop:'1rem'}} disabled={saving}>{saving?'Saving...':'Save Header'}</Button>
        </CardContent>
      </Card>

      <HeaderRow><h3>Members</h3><Button onClick={()=>{setCurrentMember({name:'', role:'', description:''}); setView('form')}}>Add Member</Button></HeaderRow>
      <Card>
        {members.map(member => (
          <div key={member._id} style={{padding:'1rem', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between'}}>
            <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
                 {member.image && <img src={getImageUrl(member.image)} alt="" style={{width:'40px', height:'40px', borderRadius:'50%', objectFit:'cover'}} />}
                 <span>{member.name} - <small>{member.role}</small></span>
            </div>
            <div>
              <Button onClick={()=>{setCurrentMember(member); setView('form')}} $variant="secondary" style={{marginRight:'0.5rem'}}>Edit</Button>
              <Button onClick={()=>deleteMember(member._id)} $variant="danger">Delete</Button>
            </div>
          </div>
        ))}
      </Card>
    </Container>
  );
};

export default TeamAdmin;
